import React, { useState } from 'react'
import moment from 'moment'
import { gql } from '@apollo/client'
import { H1, H3, H4, H6 } from '../Text'
import { merge, unionBy } from 'lodash'
import { ResizableBox } from 'react-resizable'
import { concat, filter, flow } from 'lodash/fp'
import { useMutation } from '@apollo/client'
import { Row, Col, Button, Spinner, PopoverInteractionKind, Position, Divider, Card } from '../../Elements'
import { Droppable, Draggable, DragDropContext } from 'react-beautiful-dnd'
import { Popover } from '../Popover'
import { Icon } from '@blueprintjs/core'
import { FormDate, FormInput } from '../Input'
import {
    CleanMeasurements,
    DefaultColor,
    DragDisabledColor,
    ExpandedCardNotesStyle,
    ExpandedCardScrollableColorsStyle,
    GetColorAdditionNodes,
    GuniteColor,
    GuniteFlashColor,
    NegativeOrRolledEdgeSpaIndicator,
    PebbleAppColor,
    PebbleDetailColor,
    PebblePrepColor,
    PrepCompleteIndicator,
    ReadyToApplyIndicator,
    SlurryIndicator,
    TrashIndicator,
    WarrantyColor,
    FREEFORM_POOLTYPE_ID,
    GEOMETRIC_POOLTYPE_ID,
    FreeFormOrGeometricBadge,
    WARRANTY_CATEGORY_ID,
    sortCrewRepairCardsToEnd
} from './shared'
import { AddressLinkText } from "../Address";
import '../../Components/Card/index.css'
import BuilderEmail from "../../Components/BuilderEmail";
import { CanViewJob } from "../../Components/Auth";
import { envShowCentralTexas, primaryTheme } from '../../utils/helpers'

const UPDATE_PEBBLE_WEEKLY_BOARD_CREW_INFO_ENTRY_MUTATION = gql`
    mutation UpdatePebbleWeeklyBoardCrewInfoEntry($crewInfoEntryInput: PebbleWeeklyBoardCrewInfoEntryInput ) {
        updatePebbleWeeklyBoardCrewInfoEntry(crewInfoEntryInput: $crewInfoEntryInput) {
            id
            job_detail_id
            crew_information_entry {
                id
                job_detail_id
                date
                crew {
                    id
                    name
                }
                phase_type {
                    id
                    name
                }
                job_order {
                    id
                    name
                }
                status {
                    id
                    name
                }
            }
        }
    }
`

const UPDATE_GUNITE_HISTORY_ENTRY_MUTATION = gql`
    mutation UpdateGuniteBoardHistoryEntry($details: DigitalBoardGuniteHistoryInput!) {
        updateGuniteBoardHistoryEntry(details: $details)
    }
`

const UPDATE_CREW_REPAIR_JOB_ENTRY_MUTATION = gql`
    mutation UpdateCrewRepairJobEntry($details: DigitalBoardCrewRepairJobInput!){
        updateCrewRepairJobEntry(details: $details)
    }
`

const UPDATE_WARRANTY_MUTATION = gql`
    mutation UpdateWarrantySchedule($id: ID, $schedule: DigitalBoardWarrantyScheduleInput!){
        updateWarrantySchedule(id: $id, schedule: $schedule) {
            id
        }
    }
`
const UPDATE_WARRANTY_END_MUTATION = gql`
    mutation UpdateWarrantyScheduleEnd($schedule: DigitalBoardWarrantyScheduleEndInput){
        updateWarrantyScheduleEnd(schedule: $schedule) {
            id
        }
    }
`

const UPDATE_JOB_DETAIL_DAY_VIEW_MUTATION = gql`
mutation UpdateJobDetailDayView($crew_information: DigitalBoardCrewInformationInput!) {
    updateJobDetailDayView(crew_information: $crew_information) {
        id
    }
}`

const UPDATE_SORT_ORDER_MUTATION = gql`
    mutation UpdateBoardSortOrder($items: [BoardSortItem]!) {
        updateBoardSortOrder(items: $items)
    }
`

const getJobDetailId = (binId) => binId.split('-')[1]

const getCardType = (cardId) => {
    let splitId = cardId.split('-')
    return splitId[splitId.length - 1]
}

const getBuilderEmailVariables = (item) => {

    const jobDetailType = item?.content?.job_category?.id === 3 ?
        item?.historyEntry ? 1 : 2
        : item?.content?.job_category?.id

    const phaseTypeIds = jobDetailType === 1 ? [] :
        item?.phase_id === 0 ? [item?.crewInformationEntry?.pebble_phase_type?.id] : [item?.phase_id]

    return {
        jobId: item?.content?.job?.id,
        jobDetailId: item?.content?.id,
        jobDetailType: jobDetailType,
        contactEmail: item?.content?.job?.company?.schedule_contact_email,
        supervisorEmail: item?.content?.job?.company_supervisor?.email_address,
        phaseTypeIds
    }
}

const getCrewInformationId = (draggableId) => draggableId.split('-')[2]

const PREP = 'PREP'
const GUNITE = 'Gunite'
const APP = 'APP'
const DETAIL = 'DETAIL'
const PEBBLE = 'Pebble'
const FLASH = 'FLASH'
const WARRANTY = 'WARRANTY'

const WARRANTY_BIN_SIZE = 40
const WARRANTY_BIN_COUNT = 61
const WARRANTY_BIN_IN_MINUTES = 15

const createWarrantyModelCalculateStart = (day, time) => {
    const start_date = moment(day).format('YYYY-MM-DD')

    const timeSplit = time.split(':')
    const start_time = moment({ hour: timeSplit[0], minute: timeSplit[1] }).format('HH:mm:ss')

    return start_date.toString().concat(' ', start_time.toString())
}

const createWarrantyModelCalculateEnd = (sourceDay, destDay, sourceTime, destTime, endDate) => {
    const start_date = moment(destDay).format('YYYY-MM-DD')
    const timeSplit = destTime.split(':')
    let end_time

    if (endDate !== null && sourceTime) {
        //Calculate new end time
        const start = moment(sourceDay).format('YYYY-MM-DD')
        const previousStartdate = moment(start + ' ' + sourceTime)
        const duration = moment(endDate).diff(previousStartdate, 'minutes')

        end_time = moment({ hour: timeSplit[0], minute: timeSplit[1] }).add(duration, 'minutes').format('HH:mm:ss')
    } else {
        //Set end time to the default of 1 hour later
        end_time = moment({ hour: timeSplit[0], minute: timeSplit[1] }).add(1, 'hour').format('HH:mm:ss')
    }

    return start_date.toString().concat(' ', end_time.toString())
}

const createDayViewModel = ({ sourceBin, destBin, draggableId, jobStatus, item }) => {
    let details = {}
    const job_detail_id = parseInt(getJobDetailId(draggableId))
    const pendingStatus = jobStatus.find(s => s.id === 2)

    if (destBin.type === 'Unassigned') {
        if (sourceBin?.category?.toLowerCase() === GUNITE.toLowerCase()) {
            const statusToSet = { id: pendingStatus.id, name: pendingStatus.name }
            details = {
                job_detail_id,
                crew_id: null,
                date: destBin.date,
                status: statusToSet
            }
        }
        else if (sourceBin?.category?.toLowerCase() === PEBBLE.toLowerCase()) {
            details = {
                job_detail_id,
                crew_id: null,
                date: destBin.date,
                id: parseInt(getCrewInformationId(draggableId)),
                destination_bin_id: destBin.id
            }
        }
    }
    else {
        if (destBin?.category?.toLowerCase() === GUNITE.toLowerCase()) {
            const existingStatus = item?.historyEntry?.status
            const statusToSet = { id: existingStatus?.id, name: existingStatus?.name }
            details = {
                job_detail_id,
                crew_id: destBin.crewId,
                date: destBin.date,
                status: statusToSet
            }
        }
        else if (destBin?.category?.toLowerCase() === PEBBLE.toLowerCase()) {
            details = {
                job_detail_id,
                crew_id: destBin.crewId,
                date: destBin.date,
                id: parseInt(getCrewInformationId(draggableId)),
                destination_bin_id: destBin.id
            }
        }
    }
    return { details }
}


const createWarrantyModel = ({ sourceBin, destBin, draggableId, endDate, scheduleId }) => {
    const fieldworker_id = destBin.type === 'Unassigned'
        ? null
        : destBin.workerId

    const start = destBin.type === 'Unassigned'
        ? null
        : createWarrantyModelCalculateStart(destBin.day, destBin.time)

    const end = destBin.type === 'Unassigned'
        ? null
        : createWarrantyModelCalculateEnd(sourceBin.day, destBin.day, sourceBin.time, destBin.time, endDate)

    const details = {
        schedule: {
            id: parseInt(scheduleId),
            job_detail_id: draggableId.split('-')[0],
            fieldworker_id: fieldworker_id,
            start: start,
            end: end
        },
        //Update status to Assigned if moving from Unassigned and Not Dispatched if moving to Unassigned
        status_id: sourceBin.type === 'Unassigned' ? 10 : destBin.type === 'Unassigned' ? 11 : null
    }

    return { id: scheduleId, details }
}

const createPebbleWeeklyModel = ({ sourceBin, destBin, draggableId, jobStatus }) => {
    let status_needs_to_be_updated = draggableId?.includes('new')
    let status = null

    //if the destination is an unassigned bucket, we need to update the status to the status whichever bucket they dropped the item into
    if (destBin.type === 'Unassigned') {
        status_needs_to_be_updated = true
        status = destBin.id === 'unassigned-bin'
            ? null
            : jobStatus.find(s => {
                return destBin.id === 'onHold-bin'
                    ? s.name === 'On Hold'
                    : destBin.id === 'weeping-bin'
                        ? s.name === 'Weeping'
                        : s.name === 'Awaiting Builder'
            })
        //if the destination is an assigned bucket, we want the status to be Pending IFF the source was an Unassigned Bin
    } else if (destBin.type === 'Assigned' && (sourceBin.type === 'Unassigned')) {
        status_needs_to_be_updated = true
        status = jobStatus.find(s => s.name === 'Pending')
    }

    return {
        draggableId,
        destination_bin_id: destBin.id,
        date: destBin.date,
        crew_id: destBin.crewId,
        status,
        status_needs_to_be_updated,
        awaiting_builder: destBin.id === 'awaitingBuilder-bin' || sourceBin.id === 'awaitingBuilder-bin'
    }
}


const createModel = ({ sourceBin, destBin, draggableId, statusIcons, currentStatus }) => {
    let newStatus = { id: currentStatus?.id, name: currentStatus?.name }
    let status_needs_to_be_updated = false
    let details = {}

    if (destBin.type === 'Unassigned') {
        status_needs_to_be_updated = true
        //determine new status based on the destination bin
        const idOfNewStatus = newStatus = destBin.id === 'unassigned-bin'
            ? 2 //'Pending'
            : destBin.id === 'onHold-bin'
                ? 3 //'On Hold'
                : destBin.id === 'awaitingBuilder-bin'
                    ? 19 //'Awaiting Builder'
                    : 4 //'Weeping' || 'Cave In'

        newStatus = statusIcons.find(s => s.id === idOfNewStatus)
    }
    else if (destBin.type === 'Assigned' && sourceBin.type === 'Unassigned') {
        status_needs_to_be_updated = true
        newStatus = statusIcons.find(s => s.id === 2) //'Pending'
    }

    const job_detail_id = parseInt(getJobDetailId(draggableId))

    details = {
        crew_id: destBin.crewId,
        date: destBin.date,
        awaiting_builder: destBin.id === 'awaitingBuilder-bin' || sourceBin.id === 'awaitingBuilder-bin',
        status: { id: newStatus?.id, name: newStatus?.name },
        status_needs_to_be_updated,
        job_detail_id
    }

    return { id: job_detail_id, details }
}

const Title = ({ titleStyle, titleText, collapsible = false, isCollapsed = false, onClick = null }) => (<H3
    style={titleStyle}
    onClick={onClick}>
    {collapsible && !isCollapsed && <Icon icon='caret-down' color='white' />}
    {collapsible && isCollapsed && <Icon icon='caret-right' color='white' />}
    {titleText}
</H3 >)

const warrantyNumberStyle = {
    fontSize: '9pt',
    fontWeight: 'bold',
    textAlign: 'left',
    lineHeight: '25px'
}

const jobRowStyle = {
    marginBottom: -10
}

const extraButtonStyle = {
    marginLeft: -10
}

const buttonStyle = {
    marginLeft: 0
}

const warrantyCardStyle = {
    fontSize: '9pt',
    fontWeight: 'bold'
}

const infoPopoverIconStyles = {
    background: 'white',
    marginLeft: '-3px',
    marginTop: '8px',
    borderRadius: '50%'
}

const warrantyInfoPopoverIconStyles = {
    background: 'white',
    marginLeft: '.5em',
    borderRadius: '50%',
}

const infoPopoverContentStyles = {
    background: 'white',
    padding: '5px 10px 5px 10px',
    alignItems: 'left'
}

const calculateNewEndDate = (start, width) => {

    const newDuration = (Math.round(width / WARRANTY_BIN_SIZE)) * WARRANTY_BIN_IN_MINUTES
    let newEndDate = moment(start).clone()

    newEndDate = newEndDate.add(newDuration, 'minutes')

    return newEndDate.toString()
}

const CalculateWidth = (start, end) => {
    return (moment(end).diff(moment(start), 'minutes') / WARRANTY_BIN_IN_MINUTES) * WARRANTY_BIN_SIZE
}

const CalculateMaxConstraint = (start) => {
    const maxWidth = WARRANTY_BIN_COUNT * WARRANTY_BIN_SIZE
    const widthBeforeCard = (moment(start).diff(moment({ hour: 5 }), 'minutes') / WARRANTY_BIN_IN_MINUTES) * WARRANTY_BIN_SIZE
    return (maxWidth - widthBeforeCard)
}

const InfoPopover = ({ cardContent, cardId, currentlyOpenPopover, setCurrentlyOpenPopover, contentStyles, iconStyles }) => {
    const address1 = cardContent.job.address_line_1
    const address2 = cardContent.job.address_line_2
    const city = cardContent.job.city
    const state = cardContent.job.state
    const zipCode = cardContent.job.zip_code

    const content = (
        <div style={contentStyles}>
            <Row>
                <AddressLinkText
                    line1={address1}
                    line2={address2}
                    city={city}
                    state={state}
                    zip={zipCode}
                />
            </Row>
        </div>
    )

    return (
        <Popover
            intereactionKind={PopoverInteractionKind.CLICK}
            position={Position.TOP_LEFT}
            content={content}
            isOpen={(currentlyOpenPopover?.cardId === cardId && currentlyOpenPopover?.popoverType === 'info')}
            onInteraction={(state) => setCurrentlyOpenPopover(state ? { cardId: cardId, popoverType: 'info' } : null)}
            children={<Icon
                icon='info-sign'
                style={{ ...iconStyles, cursor: 'pointer' }}
                intent='primary'
            />}
        />
    )
}

const ResizableCard = ({ content, start, end, cardId, boardState, setBoardState }) => {
    const [updateWarrantyScheduleEnd] = useMutation(UPDATE_WARRANTY_END_MUTATION)

    const onResizeUpdate = async (updateModel) => {
        await updateWarrantyScheduleEnd({
            variables: {
                schedule: updateModel
            }
        })
    }
    const onHandleResize = (start, cardId, boardState, setBoardState) => (event, result) => {
        const width = result.size && result.size.width
        const newEndDate = calculateNewEndDate(start, width)

        let item = boardState.allItems.find(i => i.id === cardId)
        item.end_date = newEndDate

        const updatedAllItems = flow(
            filter(i => i.id !== item.id),
            concat(item))(boardState.allItems)

        const updateModel = {
            schedule_id: item.schedule_id,
            job_detail_id: item.id.split('-')[0],
            end: moment(newEndDate).format('YYYY-MM-DD HH:mm:ss')
        }

        setBoardState = {
            ...boardState,
            allItems: updatedAllItems
        }

        onResizeUpdate(updateModel)
    }
    const initialWidth = CalculateWidth(start, end)
    const maxConstraint = CalculateMaxConstraint(start)
    return (
        <ResizableBox
            className='box'
            width={initialWidth}
            height='fit-content'
            minConstraints={[160, 100]}
            maxConstraints={[maxConstraint, 100]}
            handleSize={[8, 8]}
            resizeHandles={['e']}
            onResizeStop={onHandleResize(start, cardId, boardState, setBoardState)}>
            {content}
        </ResizableBox>
    )
}

const WarrantyCard = ({
    cardContent,
    width = 160,
    setAdditionalWorkerState,
    cardId,
    isResizeDisabled,
    start,
    end,
    statusImg,
    setCurrentlyOpenPopover,
    currentlyOpenPopover,
    statusPopover
}) => {
    const cardWidthStyle = {
        width: isResizeDisabled ? CalculateWidth(start, end) : width
    }
    const cardStyle = isResizeDisabled ? cardWidthStyle : {}

    return (
        <div style={cardStyle}>
            <Row>
                <Col xs={12} style={{ marginTop: '-3px' }}>
                    <span style={warrantyNumberStyle}>{cardContent.job.id}</span>
                    <span style={{ float: 'right' }}>
                        <Popover
                            content={statusPopover}
                            position={Position.RIGHT}
                            intereactionKind={PopoverInteractionKind.CLICK}
                            isOpen={(currentlyOpenPopover?.cardId === cardId
                                && currentlyOpenPopover?.popoverType === 'status')}
                            onInteraction={(state) => setCurrentlyOpenPopover((state)
                                ? { cardId: cardId, popoverType: 'status' }
                                : null)
                            }
                            children={statusImg?.status_img ?
                                <img
                                    src={statusImg.status_img}
                                    alt={statusImg.name}
                                    title={statusImg.name}
                                />
                                : statusImg?.id === 19
                                    ? <Icon icon='help' style={{ background: 'white', color: 'blue', marginTop: 5 }} iconSize={18} />
                                    : <Icon iconSize={18} icon='circle'
                                        style={{ marginTop: 5 }}
                                        intent='primary'
                                        htmlTitle={statusImg ? statusImg.name : 'N/A'} />
                            }
                        />
                    </span>
                    <span style={{ float: 'right', marginTop: '-2px' }}>
                        <span>
                            <CanViewJob>
                                <Button minimal icon='share' onClick={() => window.open(`/job/${cardContent.job.id}/edit/${cardContent.warranty_details.job_detail_id}`, "_blank")} style={buttonStyle} />
                            </CanViewJob>
                        </span>
                        {setAdditionalWorkerState &&
                            <span>
                                <Button minimal icon='new-person' onClick={() => setAdditionalWorkerState({ isOpen: true, cardId: cardId })} style={buttonStyle} />
                            </span>
                        }
                    </span>
                </Col>

            </Row>
            <Row style={jobRowStyle}>
                <Col xs={12}>
                    <H6>{cardContent.job.company && cardContent.job.company.company_name}</H6>
                </Col>
            </Row>
            <Row>
                <Col xs style={warrantyCardStyle}>
                    <span className='job-name'>{cardContent.job.name}</span>
                    {cardContent.job.city &&
                        <span>{` ${cardContent.job.city}`}</span>
                    }
                    <InfoPopover
                        cardContent={cardContent}
                        cardId={cardId}
                        currentlyOpenPopover={currentlyOpenPopover}
                        setCurrentlyOpenPopover={setCurrentlyOpenPopover}
                        iconStyles={warrantyInfoPopoverIconStyles}
                        contentStyles={infoPopoverContentStyles}
                    />
                </Col>
            </Row>
            <Row>
                <Col xs={12} style={warrantyCardStyle}>
                    {cardContent.job_types && cardContent.job_types.length > 0 && cardContent.job_types[0].name}
                </Col>
            </Row>
            <Row>
                <Col xs={12} style={warrantyCardStyle}>
                    {cardContent.warranty_details.description && cardContent.warranty_details.description.name}
                </Col>
            </Row>
        </div>
    )
}

const CondensedCard = ({
    cardContent,
    cardId,
    isDayView = false,
    scheduleNextDay = false,
    setScheduleGuniteHistoryNextDayState = null,
    statusIcon,
    setCurrentlyOpenPopover,
    currentlyOpenPopover,
    statusPopover,
    itemType,
    phaseType
}) => {
    const isGunite = cardContent.job_category.name === GUNITE
    const shouldDisplayScheduleNextDayButton = isGunite && scheduleNextDay && !isDayView
    let companyColXs = shouldDisplayScheduleNextDayButton ? 6 : 8
    let buttonColXs = 2
    let adaptedButtonStyle = shouldDisplayScheduleNextDayButton ? extraButtonStyle : buttonStyle

    const isPebble = cardContent.job_category.name === PEBBLE
    companyColXs = isPebble ? 8 : companyColXs
    buttonColXs = isPebble ? 2 : buttonColXs
    adaptedButtonStyle = isPebble ? extraButtonStyle : adaptedButtonStyle

    const isPending = itemType === 'pending'
    companyColXs = isPending ? 9 : companyColXs
    buttonColXs = isPending ? 1 : buttonColXs
    adaptedButtonStyle = isPending ? { marginLeft: '5px' } : adaptedButtonStyle

    const isGuniteOrPebble = isGunite || isPebble

    const isCrewRepair = cardContent.job_category.name === 'Warranty'

    return (
        <div>
            <Row style={jobRowStyle}>
                <Col xs={companyColXs}>
                    <H6>{cardContent.job.company && cardContent.job.company.company_name}</H6>
                </Col>
                {shouldDisplayScheduleNextDayButton &&
                    <Col xs={buttonColXs}>
                        <Button
                            minimal style={adaptedButtonStyle}
                            icon='applications'
                            onClick={() => setScheduleGuniteHistoryNextDayState({ isOpen: true, cardContent: cardContent })} />
                    </Col>}
                <Col xs={buttonColXs}>
                    <CanViewJob>
                        <Button
                            minimal style={adaptedButtonStyle}
                            icon='share'
                            onClick={() => window.open(`/job/${cardContent.job.id}/edit/${cardContent.id}`, "_blank")} />
                    </CanViewJob>
                </Col>
                <Col xs={buttonColXs}>
                    {(isGuniteOrPebble || isCrewRepair) && itemType === 'assigned' && <Popover
                        intereactionKind={PopoverInteractionKind.CLICK}
                        position={Position.RIGHT}
                        content={isDayView ? statusPopover({ phase_id: phaseType }) : statusPopover}
                        isOpen={(currentlyOpenPopover && currentlyOpenPopover.cardId === cardId && currentlyOpenPopover.popoverType === 'status')}
                        onInteraction={(state) => setCurrentlyOpenPopover(state && itemType === 'assigned' ? {
                            cardId: cardId,
                            popoverType: 'status',
                            isCrewRepair: isCrewRepair
                        } : null)}
                        children=
                        {statusIcon
                            ? <Icon
                                icon={statusIcon.icon}
                                style={statusIcon.style}
                            />
                            : <IconForNullStatus />
                        }

                    />}
                </Col>
            </Row>
            <Row style={jobRowStyle}>
                <Col xs>
                    <H6 className='job-name'>{cardContent.job.name}</H6>
                </Col>
                <Col xs>
                    <InfoPopover
                        cardContent={cardContent}
                        cardId={cardId}
                        currentlyOpenPopover={currentlyOpenPopover}
                        setCurrentlyOpenPopover={setCurrentlyOpenPopover}
                        iconStyles={infoPopoverIconStyles}
                        contentStyles={infoPopoverContentStyles}
                    />
                </Col>
            </Row>
        </div>
    )
}

const IconForNullStatus = () => <Icon
    icon='circle'
    style={
        {
            paddingLeft: '2px',
            marginLeft: '-5px',
            paddingTop: '2px',
            marginTop: '5px',
            borderRadius: '50%',
            width: '20px',
            height: '20px',
        }
    }
    intent='primary'
/>

const ExpandedCard = ({
    item,
    cardId,
    itemType,
    jobOrders,
    phaseType,
    cardContent,
    statusPopover,
    emailTemplates,
    jobOrderPopover,
    isDayView = false,
    currentlyOpenPopover,
    setPushToState = null,
    setCurrentlyOpenPopover,
    scheduleNextDay = false,
    dayViewScheduledDate = null,
    setScheduleGuniteHistoryNextDayState = null,
}) => {
    const isGunite = cardContent.job_category.name === GUNITE
    const shouldDisplayScheduleNextDayButton = isGunite && scheduleNextDay && !isDayView
    let buttonColXs = 2
    let adaptedButtonStyle = shouldDisplayScheduleNextDayButton ? extraButtonStyle : buttonStyle

    const isPebble = cardContent.job_category.name === PEBBLE
    buttonColXs = isPebble ? 2 : buttonColXs
    adaptedButtonStyle = isPebble ? extraButtonStyle : adaptedButtonStyle

    const isPending = itemType === 'pending'
    buttonColXs = isPending ? 1 : buttonColXs
    adaptedButtonStyle = isPending ? { marginLeft: '5px' } : adaptedButtonStyle

    const isCrewRepair = cardContent.job_category.name === 'Warranty'

    const crewHistoryEntry = isPebble
        ? item.crewInformationEntry
        : item.historyEntry

    const entryId = crewHistoryEntry && isGunite
        ? cardId.split('~')[1].split('-')[0]
        : cardId.split('-')[2]

    const crewRepairNotes = isCrewRepair && item.historyEntry
        ? cardContent?.warranty_details?.crew_repair_jobs?.find(i => i.id === parseInt(entryId))?.notes
        : null

    const isGuniteOrPebble = isGunite || isPebble
    const isPebbleAppPhase = (isPebble && item?.phase_id === 2)

    const isPoolTypeFreeForm = item?.content?.pool_type?.id === FREEFORM_POOLTYPE_ID
    const isPoolTypeGeometric = item?.content?.pool_type?.id === GEOMETRIC_POOLTYPE_ID

    const builderEmailVariables = getBuilderEmailVariables(item)
    const statusInfoIconStyle = {
        marginLeft: 0
    }

    return (
        <div style={{
            display: "flex",
            justifyContent: "space-between"
        }}>
            <Col>
                {cardContent.job.company &&
                    <Row style={jobRowStyle}>
                        <Col xs={10}>
                            <H6>{cardContent.job.company.company_name}</H6>
                        </Col>
                    </Row>
                }
                <Row style={jobRowStyle}>
                    <Col xs={isPebbleAppPhase ? 8 : 10}>
                        <H6 className='job-name'>{cardContent.job.name}</H6>
                    </Col>
                </Row>
                <Row style={jobRowStyle}>
                    <Col xs={10}>
                        <H6>{cardContent.job.city}</H6>
                    </Col>
                </Row>
                {cardContent.job.key_map &&
                    <Row>
                        <Col xs={10}>
                            <span>{cardContent.job.key_map}</span>
                        </Col>
                    </Row>
                }
                <Row>
                    {isGunite &&
                        <>
                            <Col xs={7}>
                                {cardContent?.job_types?.[0]?.name || ''}
                            </Col>
                            <Col xs={1}>
                                <FreeFormOrGeometricBadge isFreeForm={isPoolTypeFreeForm} isGeometric={isPoolTypeGeometric} />
                            </Col>
                            <Col xs={4}>
                                {cardContent?.status?.name === 'Ready to Apply' &&
                                    <span style={ReadyToApplyIndicator} title='Ready to Apply' />
                                }
                                {(cardContent?.gunite_details?.is_spa_negative_edge
                                    || cardContent?.gunite_details?.is_spa_rolled_edge) &&
                                    <span style={NegativeOrRolledEdgeSpaIndicator}
                                        title='Spa has Negative or Rolled Edge' />
                                }
                            </Col>
                        </>
                    }
                    {!isGunite && !isCrewRepair &&
                        <>
                            <Col xs={5}>
                                {cardContent?.job_types?.[0]?.name || ''}
                            </Col>
                            <Col xs={1}>
                                <FreeFormOrGeometricBadge isFreeForm={isPoolTypeFreeForm} isGeometric={isPoolTypeGeometric} />
                            </Col>
                            <Col xs={6}>
                                {cardContent?.pebble_details?.is_slurry_pickup &&
                                    <span style={SlurryIndicator} title='Slurry Pickup' />
                                }
                                {cardContent?.pebble_details?.is_trash_pick_up &&
                                    <span style={TrashIndicator} title='Trash Pickup' />
                                }
                                {cardContent?.pebble_details?.crew_information.findIndex(c => c?.phase_type?.id === 1 && c?.status?.id === 5 &&
                                    c?.crew?.id && c?.date !== null && moment(c?.date).isBefore(moment(), 'day')) !== -1 &&
                                    <span style={PrepCompleteIndicator} title='Prep Complete' />
                                }
                                {cardContent?.status?.name === 'Ready to Apply' &&
                                    <span style={ReadyToApplyIndicator} title='Ready to Apply' />
                                }
                            </Col>
                        </>
                    }
                    {isCrewRepair &&
                        <>
                            <Col xs={8}>
                                {cardContent?.job_types?.length > 0 ? cardContent.job_types[0].name : ''}
                            </Col>
                            <Col xs={4}>
                                {
                                    ((item?.historyEntry?.status
                                        && item?.historyEntry?.status?.name === 'Ready to Apply')
                                        || (item?.crewInformationEntry?.status
                                            && item?.crewInformationEntry.status?.name === 'Ready to Apply')) &&
                                    <span style={ReadyToApplyIndicator} title='Ready to Apply' />
                                }
                            </Col >
                        </>
                    }
                </Row >
                <Row style={jobRowStyle}>
                    <Col xs={10}>
                        {isGunite &&
                            <>
                                <p style={cardContent.gunite_details?.notes ? { margin: 0 } : {}}>{`Notes: ${cardContent.gunite_details?.notes ? '' : '---'}`}</p>
                                {cardContent.gunite_details.notes &&
                                    <p style={ExpandedCardNotesStyle}>{cardContent.gunite_details.notes}</p>
                                }
                            </>
                        }
                        {isPebble &&
                            <>
                                {cardContent?.pebble_details?.color_additions?.length > 0
                                    ? <p style={ExpandedCardScrollableColorsStyle}>
                                        {GetColorAdditionNodes(cardContent.pebble_details.color_additions)}
                                    </p>
                                    : <br />
                                }
                            </>
                        }
                        {isCrewRepair &&
                            <>
                                {crewRepairNotes
                                    ? <p style={ExpandedCardNotesStyle}>{crewRepairNotes}</p>
                                    : <br />
                                }
                            </>

                        }
                    </Col>
                </Row>
                {
                    !isCrewRepair &&
                    <>
                        <Row>
                            <Col xs={5}>
                                {`P - ${isGunite ? cardContent.plan_perimeter : cardContent.pebble_perimeter}`}
                            </Col>
                            <Col xs={7}>
                                {`D - ${[CleanMeasurements(cardContent?.depth_low),
                                CleanMeasurements(cardContent?.depth_high)].filter(m => m).join(' X ')}`}
                            </Col>
                        </Row>
                    </>
                }
                <Row>
                    {isGunite &&
                        <Col xs={7}>
                            {`SA - ${cardContent.plan_sa}`}
                        </Col>
                    }
                    {!isGunite && !isCrewRepair &&
                        <>
                            <Col xs={5}>
                                {`IA - ${cardContent.plan_ia}`}
                            </Col>
                            <Col xs={7}>
                                {`SA - ${cardContent.plan_sa}`}
                            </Col>
                        </>
                    }
                </Row>
                {
                    isDayView && dayViewScheduledDate &&
                    <Row style={{ marginTop: 5 }}>
                        <Col xs={10}>
                            <em>{moment(dayViewScheduledDate).format('MM/DD/YYYY')}</em>
                        </Col>
                    </Row>
                }
                {
                    cardContent.is_cash_on_delivery &&
                    <Row center='lg'>
                        <H1 style={{ marginTop: '15px', color: 'red' }}>* C.O.D *</H1>
                    </Row>
                }
                {isCrewRepair && crewHistoryEntry
                    && crewHistoryEntry.date
                    && !crewHistoryEntry.crew
                    && crewHistoryEntry.pebble_phase_type
                    &&
                    <Row>
                        <Col xs={10}>
                            <strong>{getPebbleWeeklyCardType(crewHistoryEntry.pebble_phase_type?.id)}</strong>
                        </Col>
                    </Row>
                }
            </Col>
            <Col style={{ marginRight: 5 }}>
                {shouldDisplayScheduleNextDayButton &&
                    <Row xs={buttonColXs}>
                        <Button
                            minimal
                            icon='applications'
                            onClick={() => setScheduleGuniteHistoryNextDayState({
                                isOpen: true,
                                cardContent: cardContent
                            })}
                            style={adaptedButtonStyle}
                        />
                    </Row>}
                <Row xs={buttonColXs}>
                    <CanViewJob>
                        <Button
                            minimal
                            icon='share'
                            onClick={() => window.open(`/job/${cardContent.job.id}/edit/${cardContent.id}`, "_blank")}
                            style={adaptedButtonStyle}
                        />
                    </CanViewJob>
                </Row>
                <Row xs={(isDayView && itemType === 'unassigned') ? 2 : buttonColXs} style={((isDayView && (isGunite || isCrewRepair)) || (!isDayView && isCrewRepair))
                    ? statusInfoIconStyle : {}}>

                    {(((isGuniteOrPebble || isCrewRepair) && itemType === 'assigned') || isDayView) &&
                        <Popover
                            content={isDayView ? statusPopover({ phase_id: phaseType }) : statusPopover({ isCrewRepair })}
                            position={Position.RIGHT}
                            intereactionKind={PopoverInteractionKind.CLICK}
                            isOpen={(currentlyOpenPopover?.cardId === cardId && currentlyOpenPopover.popoverType === 'status')}
                            onInteraction={(state) => setCurrentlyOpenPopover((state && (itemType === 'assigned' || isDayView))
                                ? { cardId: cardId, popoverType: 'status', isCrewRepair: isCrewRepair }
                                : null)
                            }
                            children={item?.statusIcon
                                ? <Icon
                                    icon={item?.statusIcon.icon}
                                    style={item?.statusIcon.style}
                                />
                                : <IconForNullStatus />
                            }
                        />
                    }
                </Row>
                <Row style={((isDayView && (isGunite || isCrewRepair)) || (!isDayView && isCrewRepair)) ? statusInfoIconStyle : {}}>
                    <InfoPopover
                        cardContent={cardContent}
                        cardId={cardId}
                        currentlyOpenPopover={currentlyOpenPopover}
                        setCurrentlyOpenPopover={setCurrentlyOpenPopover}
                        iconStyles={infoPopoverIconStyles}
                        contentStyles={infoPopoverContentStyles}
                    />
                </Row>
                {!isCrewRepair &&
                    < Row >
                        <Popover
                            content={jobOrderPopover}
                            position={Position.LEFT}
                            intereactionKind={PopoverInteractionKind.CLICK}
                            isOpen={(currentlyOpenPopover?.cardId === crewHistoryEntry?.id
                                && currentlyOpenPopover?.popoverType === 'jobOrder')}
                            onInteraction={(state) => setCurrentlyOpenPopover((state)
                                ? { cardId: crewHistoryEntry?.id, popoverType: 'jobOrder' }
                                : null)
                            }
                            targetTagName='div'
                            targetProps={{ style: { minHeight: '2em', minWidth: '2em', marginLeft: '-10px', marginTop: '.5em' } }}
                            children={
                                <JobOrderIcon
                                    itemJobOrder={crewHistoryEntry?.job_order}
                                    allJobOrders={jobOrders}
                                />
                            }
                        />
                    </Row>
                }
                {
                    (builderEmailVariables.contactEmail || builderEmailVariables.supervisorEmail) &&
                    <Row style={((isDayView && (isGunite || isCrewRepair)) || (!isDayView && isCrewRepair)) ? { width: 20, marginTop: 5 } : {
                        width: 20,
                        marginLeft: -20,
                        marginTop: 5
                    }}>
                        <BuilderEmail
                            jobId={builderEmailVariables.jobId}
                            jobDetailId={builderEmailVariables.jobDetailId}
                            contactEmail={builderEmailVariables.contactEmail}
                            supervisorEmail={builderEmailVariables.supervisorEmail}
                            jobDetailType={builderEmailVariables.jobDetailType}
                            parentTemplateData={emailTemplates}
                            buttonStyle={{ background: '#eaeaea' }}
                            isScheduleBoard={true}
                            pebblePhaseTypes={builderEmailVariables.phaseTypeIds}
                        />
                    </Row>
                }
                {isDayView && itemType === 'unassigned' && crewHistoryEntry?.id !== null &&
                    <Row>
                        <Button
                            minimal
                            icon='notifications-snooze'
                            onClick={() => setPushToState({
                                isOpen: true,
                                cardContent: cardContent,
                                cardId: cardId
                            })}
                            style={{
                                ...adaptedButtonStyle,
                                background: 'beige',
                                color: primaryTheme,
                                marginTop: 2
                            }}
                        />
                    </Row>
                }
            </Col>
        </div >
    )
}

const draggableCardDefaultCardStyle = {
    border: '1px solid lightgrey',
    borderRadius: 2,
    padding: 8,
    marginBottom: 0,
    marginRight: 8,
    backgroundColor: 'white',
    textAlign: 'left',
    height: 'fit-content',
    zIndex: 1
}

const draggableCardResizeableCardStyle = {
    maxWidth: 'unset'
}

const draggableCardNonResizeableCardStyle = {
    maxWidth: 200
}

const draggableCardHandleStyle = {
    marginBottom: '4px',
    minWidth: '160px',
    height: '20px',
    backgroundColor: primaryTheme,
    display: 'block',
    color: '#0d224a',
    textAlign: 'right',
    paddingRight: '5%',
    border: '1px solid black'
}

export const CriticalNoteIconPopover = ({
    cardId,
    currentlyOpenPopover,
    setCurrentlyOpenPopover,
    criticalNote,
    isWarranty = false
}) => {
    const content = (
        <Card style={{ margin: 0, maxWidth: '400px' }}>
            <Row>
                <Col xs={12}>
                    <H4 style={{ margin: 0 }}><Icon icon='warning-sign' color='red'
                        style={{ marginBottom: '3px' }} /> Critical Note:</H4>
                    <Divider />
                </Col>
            </Row>
            <Row>
                <Col xs={12}>
                    <p style={{ maxHeight: '5em', overflow: 'auto' }}>{criticalNote}</p>
                </Col>
            </Row>
        </Card>
    )

    //mmgBlue does not show up well for warranty cards, so we use yellow
    //use mmgBlue (primaryTheme) if mmgTX, otherwise use red (mmgAZ)
    const iconColor = isWarranty
        ? 'yellow'
        : envShowCentralTexas
            ? primaryTheme
            : 'red';

    return (
        <Popover
            intereactionKind={PopoverInteractionKind.CLICK}
            position={Position.TOP_RIGHT}
            content={content}
            isOpen={(currentlyOpenPopover && currentlyOpenPopover.cardId === cardId && currentlyOpenPopover.popoverType === 'criticalNote')}
            onInteraction={(state) => setCurrentlyOpenPopover(state ? { cardId, popoverType: 'criticalNote' } : null)}
            children={<Icon
                icon='warning-sign'
                style={{
                    marginBottom: '2px',
                    cursor: 'pointer',
                    color: iconColor
                }}
            />}
        />
    )
}

const getHandleText = (item, currentlyOpenPopover, setCurrentlyOpenPopover, isWarranty = false) => {
    const criticalNoteIcon = item?.content?.is_critical_note && item?.content?.critical_note?.trim()
        ? <CriticalNoteIconPopover cardId={item.id}
            currentlyOpenPopover={currentlyOpenPopover}
            setCurrentlyOpenPopover={setCurrentlyOpenPopover}
            criticalNote={item.content.critical_note}
            isWarranty={isWarranty}
        />
        : null

    const jobNumber = item?.content?.job?.id

    const historyEntryText = (item?.historyEntry?.type?.id && item.historyEntry.type.id !== 1 && item.historyEntry.type.id !== 9)
        ? item.historyEntry.type.name
        : ''

    const textSpanStyle = { marginLeft: '3px', float: 'left', fontWeight: 'bold', fontSize: 'medium' }

    return <>
        <span style={textSpanStyle}>{criticalNoteIcon} {jobNumber}</span>
        {historyEntryText && <span style={{ float: 'right' }}>{historyEntryText}</span>}
    </>
}

const getPebbleWeeklyCardType = (phaseId) => phaseId === 1
    ? PREP
    : phaseId === 2
        ? APP
        : phaseId === 3
            ? DETAIL
            : phaseId === 0
                ? WARRANTY
                : null

const PebbleWeeklyDraggableCard = ({
    item,
    binId,
    index,
    binType,
    jobOrders,
    isResizable,
    statusPopover,
    emailTemplates,
    isCondensedView,
    canMoveAllCards,
    jobOrderPopover,
    currentlyOpenPopover,
    setCurrentlyOpenPopover,
    cardStyle = draggableCardDefaultCardStyle
}) => {
    const cardType = getPebbleWeeklyCardType(item.phase_id)

    const showExpandedCard = !cardType
        ? (isCondensedView || binId.includes('null'))
        : binType === 'Assigned'

    const isPreviousDayAssignedCard = () => {
        if (binId.includes('null') || (isCondensedView && item.content.crewName == null)) {
            return false
        }

        if (item?.content?.job_category?.id === 3) {
            if (item?.crewInformationEntry?.date && moment(item?.crewInformationEntry?.date).isBefore(moment(), 'day')) {
                return true
            }
        } else {
            const dateToCheck = cardType === PREP
                ? 'prep_date'
                : cardType === APP
                    ? 'scheduled_app_date'
                    : cardType === DETAIL
                        ? 'detail_date'
                        : null

            if (dateToCheck
                && item?.content?.pebble_details[dateToCheck]
                && moment(item.content.pebble_details[dateToCheck]).isBefore(moment(), 'day')
            ) {
                return true
            }
        }

        return false
    }

    //Disable dragging of cards that have a status of applied
    const isDragDisabled = !canMoveAllCards && (
        ((item?.content?.job_category?.id === 3 && item?.crewInformationEntry?.status?.id === 1)
            || (item?.content?.job_category?.id === 2 && item?.content?.status?.id === 1))
        || (binType === 'Assigned' && isPreviousDayAssignedCard()))

    const cardHandleColor = isDragDisabled
        ? DragDisabledColor
        : item?.content?.job_category?.id === 3 || cardType === WARRANTY
            ? WarrantyColor
            : !cardType
                ? DefaultColor
                : cardType === FLASH
                    ? GuniteFlashColor
                    : cardType === PREP
                        ? PebblePrepColor
                        : cardType === APP
                            ? PebbleAppColor
                            : cardType === DETAIL
                                ? PebbleDetailColor
                                : GuniteColor

    const cardBackgroundColor = isDragDisabled
        ? DragDisabledColor
        : 'white'

    return (
        <Draggable
            draggableId={item.id}
            index={index}
            isDragDisabled={isDragDisabled}
        >
            {provided => {
                const style = {
                    ...cardStyle,
                    ...isResizable ? draggableCardResizeableCardStyle : draggableCardNonResizeableCardStyle,
                    ...provided.draggableProps.style,
                    backgroundColor: cardBackgroundColor
                }

                return (
                    <div
                        {...provided.draggableProps}
                        ref={provided.innerRef}
                        style={style}
                        key={index}
                    >
                        <span {...provided.dragHandleProps} style={{ ...draggableCardHandleStyle, backgroundColor: cardHandleColor }}>
                            {getHandleText(item, currentlyOpenPopover, setCurrentlyOpenPopover)}
                        </span>
                        <div>
                            {showExpandedCard
                                ? <PebbleWeeklyExpandedCard
                                    item={item}
                                    currentlyOpenPopover={currentlyOpenPopover}
                                    setCurrentlyOpenPopover={setCurrentlyOpenPopover}
                                    statusIcon={item.statusIcon}
                                    statusPopover={statusPopover}
                                    jobOrderPopover={jobOrderPopover}
                                    jobOrders={jobOrders}
                                    emailTemplates={emailTemplates}
                                />
                                : <CondensedCard
                                    item={item}
                                    cardId={item.id}
                                    cardContent={item.content}
                                    itemType={item.type}

                                    currentlyOpenPopover={currentlyOpenPopover}
                                    setCurrentlyOpenPopover={setCurrentlyOpenPopover}
                                    statusIcon={item.statusIcon}
                                    statusPopover={statusPopover}
                                />
                            }
                        </div>
                    </div>
                )
            }}
        </Draggable>
    )
}

export const JobOrderIcon = ({ itemJobOrder, allJobOrders }) => {
    // if there is a JobOrderId and a matching background, use that. Otherwise, use lightgrey. overriding linter
    // eslint-disable-next-line
    const color = itemJobOrder?.id && allJobOrders?.[itemJobOrder.id - 1]?.style?.background || 'lightgrey'
    return <div
        style={{
            backgroundColor: color,
            cursor: 'pointer',
            borderRadius: '50%',
            fontWeight: 500,
            minWidth: '2em',
            textAlign: 'center',
            padding: '3px 0'
        }}>
        <span>{itemJobOrder?.id ? itemJobOrder.name : ' # '}</span>
    </div>
}

const PebbleWeeklyExpandedCard = ({
    item, statusIcon, setCurrentlyOpenPopover, currentlyOpenPopover,
    statusPopover, jobOrderPopover, jobOrders, emailTemplates
}) => {
    const cardId = item?.id
    const isAssigned = item?.type === 'assigned'
    const isCrewRepair = item?.content?.job_category?.id === 3
    const isPoolTypeFreeForm = item?.content?.pool_type?.id === FREEFORM_POOLTYPE_ID
    const isPoolTypeGeometric = item?.content?.pool_type?.id === GEOMETRIC_POOLTYPE_ID

    const builderEmailVariables = getBuilderEmailVariables(item)

    const hasAtLeastOneDotIndicator = [
        item?.content?.pebble_details?.is_slurry_pickup,
        item?.content?.pebble_details?.is_trash_pick_up,
        item?.content?.pebble_details?.crew_information.findIndex(c =>
            c?.phase_type?.id === 1
            && c?.status?.id === 5
            && c?.crew?.id
            && c?.date !== null
            && moment(c?.date).isBefore(moment(), 'day')
        ) !== -1,
        item?.content?.status?.name === 'Ready to Apply'
    ].some(x => !!x)

    const measurementsStyle = {
        fontSize: '9pt'
    }
    return (
        <div
            style={{
                display: "flex",
                justifyContent: "space-between"
            }}
        >
            <Col>
                <Row style={jobRowStyle}>
                    <Col xs={10}>
                        <H6>{item?.content?.job?.company?.company_name}</H6>
                    </Col>
                </Row>
                <Row style={jobRowStyle}>
                    <Col xs={10}>
                        <H6 className='job-name'>{item?.content.job.name}</H6>
                    </Col>
                </Row>
                <Row style={jobRowStyle}>
                    <Col xs={10}>
                        <H6>{item?.content.job.city}</H6>
                    </Col>
                </Row>
                <Row>
                    <Col xs={10}>
                        <span>{item?.content.job.key_map}</span>
                    </Col>
                </Row>
                {isCrewRepair
                    ? <>
                        <Row>
                            <Col xs={6}>
                                {item?.content.job_types
                                    && item?.content.job_types.length > 0
                                    ? item?.content.job_types[0].name
                                    : ''}
                            </Col>
                            <Col xs={4}>
                                {item?.crewInformationEntry?.status?.name === 'Ready to Apply' &&
                                    <span style={ReadyToApplyIndicator} title='Ready to Apply' />
                                }
                            </Col>
                        </Row>
                    </>
                    : <>
                        <Row>
                            <Col xs={5}>
                                {item?.content?.job_types?.length > 0
                                    ? item?.content.job_types[0].name
                                    : ''}
                            </Col>
                            {(isPoolTypeFreeForm || isPoolTypeGeometric) &&
                                <Col xs={1}>
                                    <FreeFormOrGeometricBadge isFreeForm={isPoolTypeFreeForm} isGeometric={isPoolTypeGeometric} />
                                </Col>
                            }
                        </Row>
                        <Row style={jobRowStyle}>
                            <Col xs={10}>
                                {item?.content?.pebble_details?.color_additions?.length > 0
                                    ? <p style={ExpandedCardScrollableColorsStyle}>
                                        {GetColorAdditionNodes(item?.content.pebble_details.color_additions)}
                                    </p>
                                    : <br />
                                }
                            </Col>
                        </Row>
                        {hasAtLeastOneDotIndicator &&
                            <Row
                                style={{
                                    display: 'flex',
                                    flexWrap: 'inherit',
                                    width: '80%',
                                    padding: '5px',
                                    justifyContent: 'left',
                                    marginTop: item?.content?.pebble_details?.color_additions?.length === 0 ? '-20px' : ''
                                }}
                            >
                                {item?.content?.pebble_details?.is_slurry_pickup
                                    && <span style={SlurryIndicator} title='Slurry Pickup' />
                                }
                                {item?.content?.pebble_details?.is_trash_pick_up
                                    && <span style={TrashIndicator} title='Trash Pickup' />
                                }
                                {item?.content?.pebble_details?.crew_information.findIndex(c =>
                                    c?.phase_type?.id === 1
                                    && c?.status?.id === 5
                                    && c?.crew?.id
                                    && c?.date !== null
                                    && moment(c?.date).isBefore(moment(), 'day')
                                ) !== -1
                                    && <span style={PrepCompleteIndicator} title='Prep Complete' />
                                }
                                {item?.content?.status?.name === 'Ready to Apply'
                                    && <span style={ReadyToApplyIndicator} title='Ready to Apply' />
                                }
                            </Row>
                        }
                        <Row style={measurementsStyle}>
                            <Col xs={5}>
                                {`P - ${item?.content.pebble_perimeter}`}
                            </Col>
                            <Col xs={5}>
                                {`D - ${[CleanMeasurements(item?.content.depth_low), CleanMeasurements(item?.content.depth_high)].filter(m => m).join(' X ')}`}
                            </Col >
                        </Row >
                        <Row style={measurementsStyle}>
                            <Col xs={5}>
                                {`IA - ${item?.content.plan_ia}`}
                            </Col>
                            <Col xs={5}>
                                {`SA - ${item?.content.plan_sa}`}
                            </Col>
                        </Row>
                    </>
                }
                {
                    item?.content.is_cash_on_delivery &&
                    <Row center='lg'>
                        <H1 style={{ marginTop: '15px', color: 'red' }}>* C.O.D *</H1>
                    </Row>
                }
            </Col >
            <Col style={{ marginRight: 5 }}>
                <Row>
                    <CanViewJob>
                        <Button
                            minimal
                            icon='share'
                            style={isAssigned ? extraButtonStyle : { marginLeft: '5px' }}
                            onClick={() => window.open(`/job/${item?.content.job.id}/edit/${item?.content.id}`, "_blank")}
                        />
                    </CanViewJob>
                </Row>
                <Row>
                    {isAssigned &&
                        <Popover
                            content={statusPopover({ phase_id: item.phase_id })}
                            position={Position.RIGHT}
                            intereactionKind={PopoverInteractionKind.CLICK}
                            isOpen={(currentlyOpenPopover?.cardId === cardId
                                && currentlyOpenPopover.popoverType === 'status')}
                            onInteraction={(state) => setCurrentlyOpenPopover((state && isAssigned)
                                ? { cardId: cardId, popoverType: 'status', isCrewRepair: isCrewRepair }
                                : null)
                            }
                            children={statusIcon
                                ? <Icon
                                    icon={statusIcon.icon}
                                    style={statusIcon.style}
                                />
                                : <IconForNullStatus />
                            }
                        />
                    }
                </Row>
                <Row>
                    <InfoPopover
                        cardId={cardId}
                        cardContent={item?.content}
                        currentlyOpenPopover={currentlyOpenPopover}
                        setCurrentlyOpenPopover={setCurrentlyOpenPopover}
                        iconStyles={{ ...infoPopoverIconStyles, marginBottom: 0 }}
                        contentStyles={infoPopoverContentStyles}
                    />
                </Row>
                {isAssigned && !isCrewRepair &&
                    <Row style={{ marginTop: '.6em', marginLeft: -18 }}>
                        <Popover
                            content={jobOrderPopover}
                            position={Position.LEFT}
                            intereactionKind={PopoverInteractionKind.CLICK}
                            isOpen={(currentlyOpenPopover?.cardId === cardId
                                && currentlyOpenPopover.popoverType === 'jobOrder')}
                            onInteraction={(state) => setCurrentlyOpenPopover((state && isAssigned)
                                ? { cardId: cardId, popoverType: 'jobOrder' }
                                : null)
                            }
                            targetTagName='div'
                            targetProps={{ style: { minHeight: '2em', minWidth: '2em' } }}
                            children={
                                <JobOrderIcon
                                    itemJobOrder={item.crewInformationEntry?.job_order}
                                    allJobOrders={jobOrders}
                                />
                            }
                        />
                    </Row>
                }
                {(builderEmailVariables.contactEmail || builderEmailVariables.supervisorEmail) &&
                    <Row style={{
                        width: 20,
                        marginLeft: -20,
                        marginTop: 5
                    }}>
                        <BuilderEmail
                            jobId={builderEmailVariables.jobId}
                            jobDetailId={builderEmailVariables.jobDetailId}
                            contactEmail={builderEmailVariables.contactEmail}
                            supervisorEmail={builderEmailVariables.supervisorEmail}
                            jobDetailType={builderEmailVariables.jobDetailType}
                            pebblePhaseTypes={builderEmailVariables.phaseTypeIds}
                            parentTemplateData={emailTemplates}
                            buttonStyle={{ background: '#eaeaea' }}
                            isScheduleBoard={true}
                        />
                    </Row>
                }
            </Col >
        </div >
    )
}

const DraggableCard = ({
    item,
    binId,
    index,
    binType,
    jobOrders,
    boardState,
    isResizable,
    setBoardState,
    statusPopover,
    emailTemplates,
    setPushToState,
    canMoveAllCards,
    jobOrderPopover,
    isCondensedView,
    isDayView = false,
    isWarranty = false,
    currentlyOpenPopover,
    setCurrentlyOpenPopover,
    setAdditionalWorkerState,
    scheduleGuniteHistoryNextDayState,
    setScheduleGuniteHistoryNextDayState,
    cardStyle = draggableCardDefaultCardStyle
}) => {
    const cardType = !isWarranty && item && item.id && getCardType(item.id)

    const showExpandedCard = isDayView ? (!isCondensedView && true) : !cardType ? (isCondensedView || binId.includes('null')) : binType === 'Assigned'

    const isPreviousDayAssignedCard = () => {
        if (binId.includes('null') || (isCondensedView && item.content.crewName == null))
            return false
        if (!cardType
            && item.content.historyEntry
            && item.content.historyEntry.gunite_date
            && moment(item.content.historyEntry.gunite_date).isBefore(moment(), 'day')) {
            return true
        }

        const dateToCheck = cardType === PREP
            ? 'prep_date'
            : cardType === APP
                ? 'scheduled_app_date'
                : cardType === DETAIL
                    ? 'detail_date'
                    : null

        return !!(dateToCheck
            && item.content.pebble_details
            && item.content.pebble_details[dateToCheck]
            && moment(item.content.pebble_details[dateToCheck]).isBefore(moment(), 'day'));
    }

    //Disable dragging of cards that have a status of applied
    const isDragDisabled = !canMoveAllCards
        && (item?.content?.status?.id === 1
            || (isWarranty
                && item.start_date
                && moment(item.start_date).isBefore(moment(), 'day'))
            || (!isWarranty
                && binType === 'Assigned'
                && isPreviousDayAssignedCard())
        )

    const cardBackgroundColor = item.isPastDue
        ? '#FFCFFC'
        : isDragDisabled
            ? DragDisabledColor
            : 'white'

    const cardHandleColor = isDragDisabled
        ? DragDisabledColor
        : (isWarranty || item?.content?.job_category?.id === 3)
            ? WarrantyColor
            : cardType === FLASH
                ? GuniteFlashColor
                : cardType === PREP
                    ? PebblePrepColor
                    : cardType === APP
                        ? PebbleAppColor
                        : cardType === DETAIL
                            ? PebbleDetailColor
                            : !cardType
                                ? DefaultColor
                                : GuniteColor

    if (!cardType) draggableCardHandleStyle.color = 'white' //isWarranty

    const dayViewScheduledDate = isDayView && item.requested_scheduled_date ? item.requested_scheduled_date : null
    return (
        <Draggable
            draggableId={item.id}
            index={index}
            isDragDisabled={isDragDisabled}
        >
            {provided => {
                const style = {
                    ...cardStyle,
                    ...isResizable ? draggableCardResizeableCardStyle : draggableCardNonResizeableCardStyle,
                    ...provided.draggableProps.style,
                    backgroundColor: cardBackgroundColor
                }
                const scheduleNextDay = item.type === 'assigned'
                return (
                    <div
                        //  {...provided.dragHandleProps}
                        {...provided.draggableProps}
                        ref={provided.innerRef}
                        style={style}
                        key={index}
                    >
                        <span {...provided.dragHandleProps} style={{ ...draggableCardHandleStyle, backgroundColor: cardHandleColor }}>
                            {getHandleText(item, currentlyOpenPopover, setCurrentlyOpenPopover, isWarranty)}
                        </span>
                        <div>
                            {isWarranty && isResizable && !isDragDisabled
                                ? <ResizableCard
                                    content={<WarrantyCard cardContent={item.content}
                                        setAdditionalWorkerState={setAdditionalWorkerState}
                                        cardId={item.id} isResizeDisabled={isDragDisabled}
                                        start={item.start_date} end={item.end_date}
                                        statusImg={item.statusImg}
                                        setCurrentlyOpenPopover={setCurrentlyOpenPopover}
                                        currentlyOpenPopover={currentlyOpenPopover}
                                        statusPopover={statusPopover}
                                        itemType={item.type}
                                    />}
                                    start={item.start_date}
                                    end={item.end_date}
                                    cardId={item.id}
                                    boardState={boardState}
                                    setBoardState={setBoardState}
                                    isResizeDisabled={isDragDisabled}
                                />
                                : isWarranty
                                    ? <WarrantyCard
                                        cardContent={item.content}
                                        cardId={item.id}
                                        isResizeDisabled={isDragDisabled}
                                        start={item.start_date}
                                        end={item.end_date}
                                        statusImg={item.statusImg}
                                        setCurrentlyOpenPopover={setCurrentlyOpenPopover}
                                        currentlyOpenPopover={currentlyOpenPopover}
                                        statusPopover={statusPopover}
                                        itemType={item.type}
                                    />
                                    : showExpandedCard
                                        ? <ExpandedCard
                                            isDayView={isDayView}
                                            dayViewScheduledDate={dayViewScheduledDate}
                                            scheduleNextDay={scheduleNextDay}
                                            setScheduleGuniteHistoryNextDayState={setScheduleGuniteHistoryNextDayState}
                                            scheduleGuniteHistoryNextDayState={scheduleGuniteHistoryNextDayState}
                                            setCurrentlyOpenPopover={setCurrentlyOpenPopover}
                                            currentlyOpenPopover={currentlyOpenPopover}
                                            statusPopover={statusPopover}
                                            jobOrderPopover={jobOrderPopover}
                                            jobOrders={jobOrders}
                                            emailTemplates={emailTemplates}
                                            setPushToState={setPushToState}

                                            item={item}
                                            cardId={item.id}
                                            cardContent={item.content}
                                            statusIcon={item.statusIcon}
                                            itemType={item.type}
                                            phaseType={item.phase_id}
                                        />
                                        : <CondensedCard
                                            cardContent={item.content}
                                            cardId={item.id}
                                            isDayView={isDayView}
                                            scheduleNextDay={scheduleNextDay}
                                            setScheduleGuniteHistoryNextDayState={setScheduleGuniteHistoryNextDayState}
                                            scheduleGuniteHistoryNextDayState={scheduleGuniteHistoryNextDayState}
                                            setCurrentlyOpenPopover={setCurrentlyOpenPopover}
                                            currentlyOpenPopover={currentlyOpenPopover}
                                            statusPopover={statusPopover}
                                            statusIcon={item.statusIcon}
                                            itemType={item.type}
                                            phaseType={item.phase_id}
                                        />
                            }
                        </div>
                    </div>
                )
            }}
        </Draggable>
    )
}

export const WithDragDropContext = ({
    content, boardState, setBoardState, jobStatus, isWarranty = false, jobOrders,
    warrantyStatusImg = [], isGunite = false, isPebble = false, isDayView = false, statusIcons
}) => {
    const [updatePebbleWeeklyBoardCrewInfoEntry] = useMutation(UPDATE_PEBBLE_WEEKLY_BOARD_CREW_INFO_ENTRY_MUTATION)
    const [updateGuniteBoardHistoryEntry] = useMutation(UPDATE_GUNITE_HISTORY_ENTRY_MUTATION)
    const [updateWarrantySchedule] = useMutation(UPDATE_WARRANTY_MUTATION)
    const [updateJobDetailDayView] = useMutation(UPDATE_JOB_DETAIL_DAY_VIEW_MUTATION)
    const [updateBoardSortOrder] = useMutation(UPDATE_SORT_ORDER_MUTATION)
    const [updateCrewRepairJobEntry] = useMutation(UPDATE_CREW_REPAIR_JOB_ENTRY_MUTATION)

    const onDrop = async (props, isWarranty, isDayView, item, calculatedNewBinState = false) => {
        if (isWarranty) {
            await updateWarrantySchedule({
                variables: {
                    id: props.id,
                    schedule: props.details
                }
            }).then(res => {
                item.schedule_id = res.data.updateWarrantySchedule.id
            })
        }
        else if (isDayView) {
            if (item?.content?.job_category?.id === 1) {
                const isNewEntry = item?.id?.toString()?.includes('unassigned')
                const { crew_id, date, job_detail_id, status } = props.details
                const itemId = item.id

                if (item?.historyEntry?.crew?.id) {
                    item.historyEntry.crew.id = crew_id
                } else {
                    item.historyEntry.crew = { id: crew_id }
                }
                item.historyEntry.status = status
                item.statusIcon = statusIcons.find(si => si.id === status.id)

                await updateGuniteBoardHistoryEntry({
                    variables: {
                        details: {
                            history_entry_id: item.historyEntry.id,
                            history_entry_type_id: item?.historyEntry?.type?.id ?? null,
                            is_day_view: true,
                            crew_id,
                            date,
                            job_detail_id,
                            status
                        }
                    }
                }).then((res) => {
                    const newEntryId = res.data.updateGuniteBoardHistoryEntry
                    item.historyEntry.id = newEntryId
                    if (isNewEntry) {
                        const destinationBin = calculatedNewBinState.find(b => b.crewId === crew_id)
                        const destinationBinItemIds = destinationBin?.itemIds
                        const indexOfIdToUpdate = destinationBinItemIds.indexOf(itemId)
                        const updatedItemId = itemId.replace('unassigned', newEntryId)

                        if (indexOfIdToUpdate !== -1) {
                            item.id = updatedItemId
                            destinationBinItemIds[indexOfIdToUpdate] = updatedItemId

                        }
                    }
                    setBoardState({
                        ...boardState,
                        binsOnBoard: calculatedNewBinState
                    })
                })
            } else if (item?.content?.job_category?.id === 3) {
                if (item.historyEntry) { //gunite
                    const { crew_id, date, status } = props.details
                    await updateCrewRepairJobEntry({
                        variables: {
                            details: {
                                id: item.historyEntry.id,
                                pebble_phase_type_id: null,

                                job_detail_id: props.details.job_detail_id,
                                crew_id: props.details.crew_id,
                                date: props.details.date,
                                status: props.details.status
                            }
                        }
                    }).then((res) => {
                        item.historyEntry.id = res.data.updateCrewRepairJobEntry
                        item.historyEntry.crew = crew_id ? { id: crew_id } : null
                        item.historyEntry.date = date
                        item.historyEntry.status = status
                        item.statusIcon = statusIcons.find(si => si.id === status.id)

                        setBoardState({
                            ...boardState,
                            binsOnBoard: calculatedNewBinState
                        })
                    })
                }
                else { //pebble
                    const { crew_id, date, destination_bin_id } = props.details
                    const pebblePhaseTypeId = item.crewInformationEntry?.pebble_phase_type?.id ||
                        (destination_bin_id.includes('PREP')
                            ? 1
                            : destination_bin_id.includes('APP')
                                ? 2
                                : destination_bin_id.includes('DETAIL')
                                    ? 3
                                    : null)
                    const status = item.crewInformationEntry?.status ?
                        { id: item.crewInformationEntry.status.id, name: item.crewInformationEntry.status.name }
                        : null

                    await updateCrewRepairJobEntry({
                        variables: {
                            details: {
                                id: item.crewInformationEntry.id,
                                crew_id: crew_id,
                                status: status,
                                date: date,
                                pebble_phase_type_id: pebblePhaseTypeId
                            }
                        }
                    }).then((res) => {
                        item.crewInformationEntry.id = res.data.updateCrewRepairJobEntry
                        item.crewInformationEntry.crew = crew_id ? { id: crew_id } : null
                        item.crewInformationEntry.date = date
                        item.crewInformationEntry.pebble_phase_type = pebblePhaseTypeId ? { id: pebblePhaseTypeId } : null
                        item.stage = item.stage || destination_bin_id.split('-')[0]
                        item.isDayView = true
                        item.crewId = crew_id

                        setBoardState({
                            ...boardState,
                            binsOnBoard: calculatedNewBinState
                        })
                    })

                }

            } else {
                item.crewId = props.details.crew_id
                delete props.details['destination_bin_id']

                const { crew_id, date, id } = props.details

                await updateJobDetailDayView({
                    variables: {
                        crew_information: props.details
                    }
                }).then(() => {
                    //update existing crew information records with updated crew info
                    const itemsToUpdate = boardState.allItems.filter(i => i.content?.pebble_details?.crew_information?.find(c => c.id === id))

                    itemsToUpdate.forEach(i => {
                        const record = i.content.pebble_details.crew_information.find(c => c.id === id)
                        record.crew = crew_id === null ? null : { id: crew_id }
                        record.date = date
                    })

                    setBoardState({
                        ...boardState,
                        binsOnBoard: calculatedNewBinState
                    })
                })
            }
        }
        else {
            if (item?.content?.job_category?.id === 1) {
                await updateGuniteBoardHistoryEntry({
                    variables: {
                        details: {
                            history_entry_id: item.historyEntry.id,
                            history_entry_type_id: item?.historyEntry?.type?.id ?? null,

                            date: props?.details?.date,
                            crew_id: props?.details?.crew_id,
                            job_detail_id: props?.details?.job_detail_id,
                            awaiting_builder: props?.details?.awaiting_builder,
                            status: props.details?.status
                        }
                    }
                }).then((res) => {
                    item.historyEntry.id = res.data.updateGuniteBoardHistoryEntry
                })

            } else if (item?.content?.job_category?.id === 3) {
                if (item.historyEntry) {//Gunite
                    //Update crew repair job
                    await updateCrewRepairJobEntry({
                        variables: {
                            details: {
                                id: item.historyEntry.id,
                                pebble_phase_type_id: null,

                                crew_id: props?.details?.crew_id,
                                date: props?.details?.date,
                                job_detail_id: props?.details?.job_detail_id,
                                status: props.details?.status
                            }
                        }
                    }).then((res) => {
                        item.historyEntry.id = res.data.updateCrewRepairJobEntry
                    })
                }
                else {//pebble
                    const { crew_id, date, destination_bin_id } = props

                    const status = props.status_needs_to_be_updated
                        ? { id: props.status?.id }
                        : { id: item.crewInformationEntry.status?.id }

                    const pebblePhaseTypeId = destination_bin_id.includes('PREP')
                        ? 1
                        : destination_bin_id.includes('APP')
                            ? 2
                            : destination_bin_id.includes('DETAIL')
                                ? 3
                                : null

                    await updateCrewRepairJobEntry({
                        variables: {
                            details: {
                                id: item.crewInformationEntry.id,
                                crew_id: crew_id,
                                status: status,
                                date: date,
                                pebble_phase_type_id: pebblePhaseTypeId
                            }
                        }
                    }).then((res) => {
                        item.crewInformationEntry.id = res.data.updateCrewRepairJobEntry
                        item.crewInformationEntry.crew = crew_id ? { id: crew_id } : null
                        item.crewInformationEntry.date = date
                        item.crewInformationEntry.pebble_phase_type = pebblePhaseTypeId ? { id: pebblePhaseTypeId } : null

                        setBoardState({
                            ...boardState,
                            binsOnBoard: calculatedNewBinState
                        })
                    })
                }

            } else {
                //this else block code branch is for Pebble Weekly Board
                const isNewEntry = item?.id && item.id.toString().includes('new')

                const { draggableId, date, destination_bin_id, crew_id, status, status_needs_to_be_updated, awaiting_builder } = props

                const formattedStatus = status_needs_to_be_updated
                    ? { id: status?.id }
                    : { id: item.crewInformationEntry.status?.id }


                const crewInfoEntryInput = {
                    id: isNewEntry ? -1 : item.id,
                    job_detail_id: item.content.id,
                    phase_type_id: item.phase_id,
                    date,
                    crew_id,
                    status: formattedStatus,
                    awaiting_builder
                }

                await updatePebbleWeeklyBoardCrewInfoEntry({
                    variables: {
                        crewInfoEntryInput
                    }
                }).then((res) => {
                    if (isNewEntry) { //onDragEnd did not have the new Entry Id, so we have to reupdate board state here
                        const newEntryId = res.data.updatePebbleWeeklyBoardCrewInfoEntry.id

                        item.id = newEntryId

                        const destinationBin = calculatedNewBinState.find(b => b.id === destination_bin_id)
                        const destinationBinItemIds = destinationBin?.itemIds

                        const indexOfIdToUpdate = destinationBinItemIds.indexOf(draggableId)
                        if (indexOfIdToUpdate !== -1) {
                            destinationBinItemIds[indexOfIdToUpdate] = newEntryId
                        }
                    }

                    item.crewInformationEntry = res.data.updatePebbleWeeklyBoardCrewInfoEntry.crew_information_entry
                    if (status_needs_to_be_updated) item.statusIcon = status

                    if (awaiting_builder && status?.id === 19) {
                        item.content.status = status //Set overall status to Awaiting Builder
                    }

                    //update content level crew information records for the item that was just updated
                    const itemsToUpdate = boardState.allItems.filter(i => i.content?.pebble_details?.crew_information?.find(c => c.id === item.crewInformationEntry.id))

                    itemsToUpdate.forEach(i => {
                        const record = i.content.pebble_details.crew_information.find(c => c.id === item.crewInformationEntry.id)
                        record.status = item.crewInformationEntry.status
                        record.crew = item.crewInformationEntry.crew
                        record.date = item.crewInformationEntry.date
                    })

                    setBoardState({
                        ...boardState,
                        binsOnBoard: calculatedNewBinState
                    })
                })
            }
        }
    }

    const setDroppableProp = ({ draggableId }) => {
        //Determine whether bin is a droppable location if multiple stages are present
        const matchingItem = draggableId
            ? boardState.allItems.find(i => i.id === draggableId)
            : null

        const isPebbleWeekly = matchingItem?.phase_id >= 0

        const isHistoryEntry = matchingItem && matchingItem.historyEntry
        const isDayHistoryEntry = isHistoryEntry && matchingItem.historyEntry.type && matchingItem.historyEntry.type.id > 1 && matchingItem.historyEntry.type.id < 9

        const cardType = isPebbleWeekly
            ? getPebbleWeeklyCardType(matchingItem.phase_id)
            : draggableId && getCardType(draggableId)

        const newBinState = []

        boardState.binsOnBoard.forEach(b => {
            let shouldDisableDrop = false
            if (isHistoryEntry && isDayHistoryEntry) {
                shouldDisableDrop = (b.id === 'unassigned-bin' || b.id === 'weeping-bin' || b.id === 'onHold-bin')
            } else if (isPebbleWeekly && b.id === 'weeping-bin' && cardType !== APP && cardType !== WARRANTY) {
                //non-Application-phase Pebble Weekly cards cannot be put in the weeping bin
                shouldDisableDrop = true
            } else if (matchingItem?.isDayView && matchingItem?.crewInformationEntry) {
                shouldDisableDrop = b.stage && matchingItem?.stage && (b.stage !== matchingItem.stage)
            } else if (b.id === 'awaitingBuilder-bin' && (cardType === WARRANTY || (isHistoryEntry && matchingItem.content?.job_category?.id === 3))) {
                //Disable dropping crew repair jobs into awaiting builder bucket
                shouldDisableDrop = true
            } else {
                shouldDisableDrop = cardType && b.stage && (cardType !== b.stage) && cardType !== 'FLASH' && cardType !== WARRANTY
            }

            newBinState.push({
                ...b,
                isDropDisabled: shouldDisableDrop
            })
        })

        setBoardState({
            ...boardState,
            binsOnBoard: newBinState
        })
    }

    const onDragStart = start => {
        setDroppableProp(start)
    }

    const onDragEnd = result => {
        setDroppableProp({})
        const { destination, source, draggableId } = result

        let item = boardState.allItems.find(a => a.id === draggableId)

        if (!destination //destinationIsNotABin
            || (destination.droppableId === source.droppableId && destination.index === source.index) //cardDidNotChangePosition
            || (isPebble && destination.droppableId === 'weeping-bin' && item?.phase_id !== 2) //dropped Non-Application-Phase Onto Weeping Bin - can only allow Application Phase Cards in Weeping Bin
        ) {
            return
        }

        const sourceBin = boardState.binsOnBoard.find(b => b.id === source.droppableId)
        const destBin = boardState.binsOnBoard.find(b => b.id === destination.droppableId)

        const updateBoardJobOrderAndMapItems = (itemData) => {
            //This function takes a collection of items and uses their index in their bin to calculate the item's new job order value.
            //The item data used on the board is then updated. Finally, we return a collection of item ids with their new order and 
            //category, intended to be used for the updateBoardSortOrder mutation
            //We do NOT expect to recieve ANY Crew Repair Entries in this process since they do NOT have an individually tracked Job Order
            const mappedItems = []
            if (isGunite || isPebble) { //only setup to work for gunite and pebble drag-and-drop boards
                const categoryAsString = isGunite ? GUNITE : PEBBLE

                itemData.forEach((item, idx) => {
                    const idOfNewJobOrderForItem = Math.min(idx + 1, 8)
                    const newJobOrderValue = jobOrders.find(o => o.id === idOfNewJobOrderForItem) || null

                    //Find the board item to update its Job order Value (boardItem will be by-reference instead of by-value)
                    const boardItem = boardState.allItems.find(bi => bi.id === item.id)
                    if (isGunite) {
                        boardItem.historyEntry.job_order = newJobOrderValue
                    } else if (isPebble) {
                        boardItem.crewInformationEntry.job_order = newJobOrderValue
                    }
                    const theId = isGunite
                        ? item.historyEntry.id
                        : item.crewInformationEntry.id
                    const idDetails = { id: theId, order: idOfNewJobOrderForItem, category: categoryAsString }
                    mappedItems.push(idDetails)
                })
            }
            return mappedItems
        }

        const mapItemsAndUpdateJobOrderFromItemIds = (itemIds) => {
            //This function takes a list of itemIds and uses their index in their bin to calculate the item's new job order value.
            //If clearJobOrder is true (e.g. when the bin is NOT for a crew and thus order is not tracked), then we always set job order to null
            //The item data used on the board is then updated. Finally, we return a collection of item ids with their new order and 
            //category, intended to be used for the updateBoardSortOrder mutation
            //We do NOT include Crew Repair Entries in this process since they do NOT have an individually tracked Job Order
            const mappedItems = []
            if (isGunite || isPebble) { //only setup to work for gunite and pebble drag-and-drop boards
                const categoryAsString = isGunite ? GUNITE : PEBBLE

                //now we want to filter out the Crew Repair entries before determining new job orders, which is done by checking if the item has the corresponding job detail
                const allItemsFromIds = itemIds.map(id => boardState.allItems.find(item => item.id === id))
                const filteredItemsFromIds = allItemsFromIds.filter(item => ((isGunite && item?.content?.gunite_details) || (isPebble && item?.content?.pebble_details)))

                filteredItemsFromIds.forEach((theItem, idx) => {
                    const idOfNewJobOrderForItem = Math.min(idx + 1, 8)
                    const newJobOrderValue = jobOrders.find(o => o.id === idOfNewJobOrderForItem) || null

                    if (isGunite) {
                        theItem.historyEntry.job_order = newJobOrderValue
                    } else if (isPebble) {
                        theItem.crewInformationEntry.job_order = newJobOrderValue
                    }
                    const theId = isGunite
                        ? theItem.historyEntry.id
                        : theItem.crewInformationEntry.id
                    const idDetails = { id: theId, order: idOfNewJobOrderForItem, category: categoryAsString }
                    mappedItems.push(idDetails)
                })
            }
            return mappedItems
        }

        //#region Moving within same bin
        if (sourceBin === destBin) {
            //All ids for the bin
            const binItemIds = Array.from(sourceBin.itemIds)

            //Reorder based on the drag drop indeces
            binItemIds.splice(source.index, 1)
            binItemIds.splice(destination.index, 0, draggableId)

            //Map them into full items
            const allItemsFromIds = binItemIds.map(id => boardState.allItems.find(item => item.id === id))

            //Sort the items to move crew repair items to the end
            allItemsFromIds.sort(sortCrewRepairCardsToEnd)

            //If this bin is a Crew-Associated bin, we update Job Orders appropriately
            if (sourceBin.crewId || destBin.crewId) {
                //Full Items, in order, but excluding Crew Repair Entries
                const orderedNonCrewRepairItems = allItemsFromIds.filter(i => i?.content?.job_category?.id !== WARRANTY_CATEGORY_ID)
                const itemsMappedForUpdateBoardSortOrder = updateBoardJobOrderAndMapItems(orderedNonCrewRepairItems)

                updateBoardSortOrder({
                    variables: {
                        items: itemsMappedForUpdateBoardSortOrder
                    }
                })
            }

            //Only Item Ids, In Correct Updated Order
            const orderedAllBinItemIds = allItemsFromIds.map(i => i.id)

            //setup new bin and set board's bin state
            const newBin = {
                ...sourceBin,
                itemIds: orderedAllBinItemIds
            }
            const newBinState = flow(
                filter(b => b.id !== newBin.id),
                concat(newBin))(boardState.binsOnBoard)

            setBoardState({
                ...boardState,
                binsOnBoard: newBinState
            })

            return
        }
        //#endregion Moving within same bin
        //#region Moving from one bin to another
        else {
            const sourceBinIds = Array.from(sourceBin.itemIds)
            const indexOfSourceItemToRemove = sourceBinIds.findIndex(i => i === draggableId)
            if (indexOfSourceItemToRemove >= 0) {
                sourceBinIds.splice(indexOfSourceItemToRemove, 1)
            }

            //Map them into full items
            const sourceBinItemsFromIds = sourceBinIds.map(id => boardState.allItems.find(item => item.id === id))
            //Sort the items to move crew repair items to the end
            sourceBinItemsFromIds.sort(sortCrewRepairCardsToEnd)
            //Only Item Ids, In Correct Updated Order
            const orderedSourceBinItemIds = sourceBinItemsFromIds.map(i => i.id)
            //setup new source bin state
            const newSourceBin = {
                ...sourceBin,
                itemIds: orderedSourceBinItemIds
            }


            const destBinIds = Array.from(destBin.itemIds)
            destBinIds.splice(destination.index, 0, draggableId)
            //Map them into full items
            const destBinItemsFromIds = destBinIds.map(id => boardState.allItems.find(item => item.id === id))
            //Sort the items to move crew repair items to the end
            destBinItemsFromIds.sort(sortCrewRepairCardsToEnd)
            //Only Item Ids, In Correct Updated Order
            const orderedDestBinItemIds = destBinItemsFromIds.map(i => i.id)
            //setup new dest Bin state
            const newDestBin = {
                ...destBin,
                itemIds: orderedDestBinItemIds
            }


            let newBinState = flow(
                filter(b => b !== sourceBin && b !== destBin),
                concat(newSourceBin),
                concat(newDestBin))(boardState.binsOnBoard)

            if (destBin.type === 'Assigned') {
                item.type = 'assigned'
            } else if (destBin.type === 'Unassigned') {
                item.type = isDayView ? 'unassigned' : 'pending'
            }

            if (sourceBin.crewId || destBin.crewId) {
                const sourceItemsMappedForUpdateBoardSortOrder = sourceBin.crewId
                    ? mapItemsAndUpdateJobOrderFromItemIds(sourceBinIds)
                    : []

                const destItemsMappedForUpdateBoardSortOrder = destBin.crewId
                    ? mapItemsAndUpdateJobOrderFromItemIds(destBinIds)
                    : []

                const allMappedItemsForUpdateBoardSortOrder = sourceItemsMappedForUpdateBoardSortOrder.concat(destItemsMappedForUpdateBoardSortOrder)

                updateBoardSortOrder({
                    variables: {
                        items: allMappedItemsForUpdateBoardSortOrder
                    }
                })
            }

            const model = isWarranty
                ? createWarrantyModel({ sourceBin, destBin, draggableId, endDate: item.end_date, scheduleId: item.schedule_id })
                : isDayView
                    ? createDayViewModel({ sourceBin, destBin, draggableId, jobStatus, item })
                    : isGunite
                        ? createModel({ sourceBin, destBin, draggableId, statusIcons, currentStatus: item?.historyEntry?.status })
                        : createPebbleWeeklyModel({ sourceBin, destBin, draggableId, jobStatus })

            if (isWarranty) {
                item.content.warranty_details.schedule = unionBy([model.details.schedule], item.content.warranty_details.schedule, 'id')
                item.start_date = model.details.schedule.start ? moment(model.details.schedule.start) : null
                item.end_date = model.details.schedule.end ? moment(model.details.schedule.end) : null
                if (model.details.status_id !== null) {
                    item.statusImg = warrantyStatusImg.find(img => img.id === model.details.status_id)
                }

            }
            else if (isGunite) {
                const { status_needs_to_be_updated, ...modelToMerge } = { ...model.details }
                if (status_needs_to_be_updated) {
                    item.statusIcon = statusIcons.find(si => si.id === modelToMerge?.status?.id)
                    item.historyEntry.status = modelToMerge.status
                } else {
                    modelToMerge.status = item.historyEntry.status
                }

                merge(item.content, modelToMerge)
            }
            else if (isDayView) {


                merge(item.content, model.details)

            } else {
                //pebbble weekly
                if (model.status_needs_to_be_updated) {
                    item.statusIcon = model.status
                }
                //Move associated cards if card in question is moving to or from Awaiting Builder
                if (item.content?.job_category?.id === 2 && (sourceBin.id === 'awaitingBuilder-bin' || destBin.id === 'awaitingBuilder-bin')) {
                    //Find job detail id of card
                    const jobDetailId = boardState.allItems.find(b => b.id === draggableId)?.content?.id

                    //Find associated cards that aren't completed or applied or the currently dragged card
                    let itemsToMove = boardState.allItems.filter(b => b?.content?.id === jobDetailId && b?.id !== draggableId
                        && !([1, 5].includes(b?.crewInformationEntry?.status?.id) && b?.crewInformationEntry.date))

                    itemsToMove.forEach(i => {
                        i.content.status = model.status //set overall status
                        i.type = 'pending'
                        if (i.crewInformationEntry) {
                            i.crewInformationEntry.status = jobStatus.find(s => s.id === 2) //set inner crew status to pending
                            i.crewInformationEntry.date = null
                            i.crewInformationEntry.crew = null
                            i.crewInformationEntry.foreman = null
                        }

                        //Remove from current bin
                        const indexOfBinToRemoveCardFrom = newBinState.findIndex(b => b.itemIds.includes(i.id))
                        if (indexOfBinToRemoveCardFrom !== -1)
                            newBinState[indexOfBinToRemoveCardFrom].itemIds = newBinState[indexOfBinToRemoveCardFrom].itemIds.filter(item => item !== i.id)

                        //Add to new bin
                        if (sourceBin.id === 'awaitingBuilder-bin') {
                            //move to unassigned
                            const unassignedBin = newBinState.find(b => b.id === 'unassigned-bin')
                            unassignedBin.itemIds.push(i.id)
                        }
                        else {
                            //move to awaitingBuilder
                            const awaitingBuilderBin = newBinState.find(b => b.id === 'awaitingBuilder-bin')
                            awaitingBuilderBin.itemIds.push(i.id)
                        }
                    })
                }
            }

            setBoardState({
                ...boardState,
                binsOnBoard: newBinState
            })

            onDrop(model, isWarranty, isDayView, item, newBinState) //pass in newBinState so that, if necessary, we can use it to update board state after network calls
        }
        //#endregion Moving from one bin to another
    }

    return (
        <DragDropContext onDragEnd={onDragEnd} onDragStart={onDragStart}>
            {content}
        </DragDropContext>
    )
}

const fallbackDroppableBinTitleStyle = {
    fontWeight: 'bold',
    padding: '8px',
}

const fallbackDroppableBinBinStyle = {
    margin: '8px',
    border: '1px solid lightgray',
    borderRadius: '2px',
    width: 250,
    display: 'flex',
    flexDirection: 'column'
}

const droppableBinTimeStyle = {
    color: 'lightgray',
    overflow: 'hidden',
    position: 'absolute',
    textOrientation: 'mixed',
    writingMode: 'vertical-lr'
}

const baseCardAreaStyle = {
    //flexGrow: 1,
    position: 'relative',
    transition: 'background-color 0.2 ease'
}

const wrappableCardAreaStyling = {
    display: 'flex',
    flexWrap: 'wrap'
}

const resizeableCardAreaStyling = {
    paddingTop: 4,
    paddingBottom: 0,
    paddingLeft: 4,
    height: 'fit-content',
    minHeight: 100
}

export const DroppableBin = ({ id, title, items = [], binStyle = fallbackDroppableBinBinStyle, titleStyle = fallbackDroppableBinTitleStyle,
    cardStyle, isDropDisabled = false, loading = false, binType, isCondensedView, direction = 'vertical', shouldWrap = 'false',
    showDropColor = true, isResizable = false, isWarranty = false, setAdditionalWorkerState = null,
    setScheduleGuniteHistoryNextDayState = null, scheduleGuniteHistoryNextDayState = null, boardState = null, jobOrderPopover = null, jobOrders,
    setBoardState = null, time = null, canMoveAllCards, isDayView = false, setCurrentlyOpenPopover, currentlyOpenPopover = null, statusPopover = null,
    liveFiltering = false, filteringPredicate, verticalGrow = false, isPebbleWeekly = false, setPushToState = null, collapsible = false, emailTemplates }) => {

    const [keyword, setKeyword] = useState('')
    const [requestDateQuery, setRequestDateQuery] = useState(null)
    const [isExpanded, setIsExpanded] = useState(collapsible)

    const nonResizeableCardAreaStyling = {
        padding: 8,
        height: '100%',
        minHeight: 160,
        alignContent: 'flex-start'
    }

    const filteredItems = !liveFiltering || (!keyword && !requestDateQuery)
        ? items
        : items.filter(i => filteringPredicate(i, keyword, requestDateQuery))

    const showCollapsed = (collapsible && isExpanded) || collapsible === false

    const collapseTitleStyle = !collapsible ? titleStyle : {
        ...titleStyle,
        cursor: 'pointer'
    }

    return (
        <div style={binStyle} id={id} {...(isDayView && id === 'unassigned-bin' ? { className: 'dayViewUnassigned' } : {})}>
            {title && <Title
                titleStyle={collapseTitleStyle}
                titleText={title}
                collapsible={collapsible}
                isCollapsed={!isExpanded}
                onClick={() => setIsExpanded(!isExpanded)} />}
            {loading && <Spinner />}
            {!loading && showCollapsed && <Droppable droppableId={id} direction={direction} isDropDisabled={isDropDisabled}>
                {(provided, snapshot) => {
                    const style = {
                        ...provided.droppableProps.style,
                        ...baseCardAreaStyle,
                        ...shouldWrap && wrappableCardAreaStyling,
                        ...isResizable ? resizeableCardAreaStyling : nonResizeableCardAreaStyling,
                        backgroundColor: showDropColor && snapshot.isDraggingOver ? 'skyblue' : 'white',
                        flexDirection: verticalGrow ? 'column' : 'unset'
                    }
                    return (
                        <>
                            {liveFiltering && <Row style={{ textAlign: 'center' }}>
                                <Col xs={12} sm={12} md={5} lg={5} lgOffset={1} mdOffset={1} style={{ paddingLeft: '1em' }}>
                                    <FormInput
                                        value={keyword}
                                        onChange={(evt) => {
                                            setKeyword(evt.target.value)
                                            if (!evt.target?.value || (evt.target.value?.trim()).length === 0) {
                                                setKeyword('')
                                            }
                                        }}
                                        placeholder='name, num, cust'
                                        type='search'
                                    />
                                </Col>
                                <Col xs={12} sm={12} md={5} lg={5} style={{ paddingLeft: '1em' }}>
                                    <FormDate
                                        value={requestDateQuery}
                                        placeholder='req date MM/DD/YY'
                                        disablePopover
                                        onChange={(e) => {
                                            let date = moment(e, ['M/D/YYYY', 'M/D/YY', 'YYYY-M-D', 'YY-M-D'], true)

                                            if (date.isValid()) {
                                                setRequestDateQuery(date.format('YYYY-MM-DD'))
                                            } else {
                                                setRequestDateQuery(null)
                                            }
                                        }}
                                        onBlur={(e) => {
                                            const value = e && e.currentTarget ? e.currentTarget.value : e

                                            let date = moment(value, ['M/D/YYYY', 'M/D/YY', 'YYYY-M-D', 'YY-M-D'], true)
                                            let fieldDate = moment(requestDateQuery)

                                            if ((date.format('YYYY-MM-DD') === fieldDate.format('YYYY-MM-DD')) || (requestDateQuery === undefined && value === '')) {
                                                return
                                            }

                                            if (date.isValid()) {
                                                setRequestDateQuery(date.format('YYYY-MM-DD'))
                                            } else {
                                                setRequestDateQuery(null)
                                            }
                                        }}
                                        inputProps={{
                                            style: {
                                                borderRadius: '30px'
                                            }
                                        }}
                                    />
                                </Col>
                            </Row>
                            }
                            <div {...provided.droppableProps} ref={provided.innerRef} style={style}>
                                {time &&
                                    <span style={droppableBinTimeStyle}>{time}</span>
                                }
                                {filteredItems.map((item, index) => isPebbleWeekly
                                    ? <PebbleWeeklyDraggableCard
                                        item={item}
                                        cardStyle={cardStyle}
                                        index={index}
                                        binId={id}
                                        binType={binType}
                                        isCondensedView={isCondensedView}
                                        isResizable={isResizable}
                                        canMoveAllCards={canMoveAllCards}
                                        currentlyOpenPopover={currentlyOpenPopover}
                                        setCurrentlyOpenPopover={setCurrentlyOpenPopover}
                                        statusPopover={statusPopover}
                                        jobOrderPopover={jobOrderPopover}
                                        jobOrders={jobOrders}
                                        key={item.id}
                                        emailTemplates={emailTemplates}
                                    />
                                    : <DraggableCard
                                        binId={id}
                                        item={item}
                                        index={index}
                                        key={item.id}
                                        binType={binType}
                                        cardStyle={cardStyle}
                                        boardState={boardState}
                                        isWarranty={isWarranty}
                                        isResizable={isResizable}
                                        setBoardState={setBoardState}
                                        canMoveAllCards={canMoveAllCards}
                                        isCondensedView={isCondensedView}
                                        setAdditionalWorkerState={setAdditionalWorkerState}
                                        setScheduleGuniteHistoryNextDayState={setScheduleGuniteHistoryNextDayState}
                                        scheduleGuniteHistoryNextDayState={scheduleGuniteHistoryNextDayState}
                                        isDayView={isDayView}
                                        setCurrentlyOpenPopover={setCurrentlyOpenPopover}
                                        currentlyOpenPopover={currentlyOpenPopover}
                                        statusPopover={statusPopover}
                                        jobOrderPopover={jobOrderPopover}
                                        jobOrders={jobOrders}
                                        setPushToState={setPushToState}
                                        emailTemplates={emailTemplates}
                                    />
                                )}
                                {provided.placeholder}
                            </div>
                        </>
                    )
                }}
            </Droppable>
            }
        </div>
    )
}
